import type { SchemaData, SerializedHistory } from '@lemonade-hq/chat-engine';
import { NodeStateStatus, NodeType, SessionStatus } from '@lemonade-hq/chat-engine';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { devWarn } from '@lemonade-hq/ts-helpers';
import { useCallback, useRef } from 'react';
import type { ChatEngineSession, PageRenderResponse } from './page_mechanism.client';
import { PageMechanismClient } from './page_mechanism.client';
import type { UseChatServerPersistenceProps, UseChatServerPersistenceReturn } from './usePersistenceHooksTypes';
import type {
  WizardPersistenceContextSubmissionOptions,
  WizardPersistenceLoadSessionProps,
} from './wizard_persistence.context';

// istanbul ignore next -- @preserve - partially tested
const SERVER_TIMEOUT: Milliseconds = (process.env.ENV ?? 'production') === 'production' ? 16_000 : 40_000;

// MAIN

export const usePagePersistence = <TPresentation = undefined>({
  abortController,
  setSession,
  setUiState,
  sessionId,
  currentStepId,
  serverBaseUrl: _serverBaseUrl,
  scriptName: scriptNameProp,
  redirectFn,
  serverTimeoutOverride: serverTimeoutOverrideProp,
}: UseChatServerPersistenceProps<TPresentation>): UseChatServerPersistenceReturn<TPresentation> => {
  const pageMechanismClient = useRef<PageMechanismClient<TPresentation>>();
  const sessionTimeoutOverrideRef = useRef<Milliseconds | undefined>(serverTimeoutOverrideProp);

  const transformPageToSession = useCallback(
    (page: PageRenderResponse<TPresentation>, scriptName: string): ChatEngineSession<TPresentation> => {
      return {
        auth: page.auth,
        schema: page.updateSchema,
        assets: page.assets,
        sessionId: 'noSessionId',
        sessionStatus: SessionStatus.Pending,
        script: {
          name: scriptName,
          version: '1',
          engineVersion: 'page-mechanism',
          id: 'noSessionId',
        },
        history: [
          {
            renderedStep: page.renderResult,
            status: NodeStateStatus.Pending,
            type: NodeType.Render,
            name: scriptName,
            id: scriptName,
          },
        ],
      };
    },
    [],
  );

  // SETUP - INIT
  const initSession = useCallback(
    async ({
      serverTimeoutOverride,
      accessTokenOverride,
      emailCode,
      scriptName,
      clearPreviousSession = false,
      serverBaseUrl: sessionServerBaseUrl,
      ...loadSessionProps
    }: WizardPersistenceLoadSessionProps) => {
      if (clearPreviousSession) {
        setSession(null);
      }

      pageMechanismClient.current = new PageMechanismClient<TPresentation>({
        serverBaseUrl: sessionServerBaseUrl,
        accessTokenOverride,
        emailCode,
      });
      sessionTimeoutOverrideRef.current = serverTimeoutOverride;

      setUiState(val => {
        return { ...val, isLoadingInitialServerData: true };
      });

      const fetchedPage = await pageMechanismClient.current.getPage(
        { ...loadSessionProps, pageName: scriptName },
        {
          timeout: sessionTimeoutOverrideRef.current ?? SERVER_TIMEOUT,
        },
      );

      setSession(transformPageToSession(fetchedPage, scriptName));

      setUiState(val => {
        return { ...val, isLoadingInitialServerData: false };
      });
    },
    [setSession, setUiState, transformPageToSession],
  );

  // ACTIONS - SUBMIT ANSWERS
  const submitAnswersToServer = useCallback(
    async (answers: SchemaData, options?: WizardPersistenceContextSubmissionOptions) => {
      /* istanbul ignore if -- @preserve - missing test */
      if (pageMechanismClient.current == null) {
        /* istanbul ignore next -- @preserve - missing test */
        devWarn('Page mechanism client not initialized');
        /* istanbul ignore next -- @preserve - missing test */
        return { history: [] as SerializedHistory<TPresentation>, success: false };
      }

      if (sessionId == null || currentStepId == null) {
        devWarn('Cannot submit without a session ID or current step ID');
        return { history: [] as SerializedHistory<TPresentation>, success: false };
      }

      abortController.current = new AbortController();

      if (options?.resync) {
        const fetchedSession = await pageMechanismClient.current.submitAnswers(
          {
            answers,
            pageName: scriptNameProp,
          },
          {
            timeout: sessionTimeoutOverrideRef.current ?? SERVER_TIMEOUT,
            signal: abortController.current.signal,
          },
        );

        if (fetchedSession?.renderResult == null) {
          setUiState(val => {
            return { ...val, isSubmittingToServer: false };
          });
          return { history: [] as SerializedHistory<TPresentation>, success: false };
        }

        const session = transformPageToSession(fetchedSession, scriptNameProp);

        setSession(session);
        setUiState(val => {
          return { ...val, isSubmittingToServer: false };
        });
        return { history: session.history, success: true };
      } else {
        const finishResponse = await pageMechanismClient.current.finish(
          {
            pageName: scriptNameProp,
          },
          {
            timeout: sessionTimeoutOverrideRef.current ?? SERVER_TIMEOUT,
            signal: abortController.current.signal,
          },
        );

        if (finishResponse.renderResult?.redirectTo != null) {
          await redirectFn?.(finishResponse.renderResult.redirectTo);
        }

        return { history: [] as SerializedHistory<TPresentation>, success: false };
      }
    },
    [
      currentStepId,
      scriptNameProp,
      sessionId,
      setSession,
      setUiState,
      abortController,
      transformPageToSession,
      redirectFn,
    ],
  );

  return {
    initSession,
    submitAnswersToServer,
  };
};
